import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateObligationImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

type UpdateObligationImpactInput = {
  Id: string;
  Description: string;
  ImpactRating: number;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateObligationImpactTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationImpact.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationImpact.getByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationImpact.getById.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `obligation`.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.pathKey(),
        });
      },
    }),
  });

  return {
    updateObligationImpact: async (
      variables: UpdateObligationImpactInput
    ): Promise<UpdateObligationImpactMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};

function mapTrpcResponseToGraphQL(): UpdateObligationImpactMutation {
  return {
    update_obligation_impact: {
      __typename: 'obligation_impact_mutation_response',
      affected_rows: 1,
    },
  };
}
